import { useEffect, useMemo, useState, type FormEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usePeople } from "@/hooks/usePeople";
import { useProjectAvailability } from "@/hooks/useProjectPeople";
import { useProjects } from "@/hooks/useProjects";
import {
  bookingCategoryLabels,
  isoDate,
  useDeleteVehicleBooking,
  useSaveVehicleBooking,
  useVehicleBookingsInRange,
  useVehicles,
  type VehicleBooking,
  type VehicleBookingCategory,
} from "@/hooks/useVehicles";
import { formatDate } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import { formatProjectName } from "@/lib/format-project-name";
import { bookingsOverlap, isoToZeroDate } from "@/lib/vehicle-bookings";

export interface VehicleBookingDefaults {
  vehicleID?: string;
  projectID?: string;
  category?: VehicleBookingCategory;
  startDate?: string;
  endDate?: string;
}

interface VehicleBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Booking to edit; omit to create one */
  booking?: VehicleBooking | null;
  defaults?: VehicleBookingDefaults;
  /** Project tab: always a booking for this project */
  fixedProjectId?: string;
}

/** Label of a booking in lists and conflict warnings */
export function bookingLabel(booking: VehicleBooking) {
  if (booking.category === "project") {
    return booking.project
      ? formatProjectName(booking.project.name, booking.project.eventStartDate)
      : "Project";
  }
  return booking.title || bookingCategoryLabels[booking.category];
}

const personName = (person: { firstName: string; lastName: string }) =>
  `${person.firstName} ${person.lastName}`;

/**
 * Drivers of a booking: one name when the same person drives both ways,
 * else "Outbound: A · Return: B". Null when no driver is set.
 */
export function formatBookingDrivers(booking: VehicleBooking) {
  const outbound = booking.driver ? personName(booking.driver) : null;
  if (booking.category !== "project") return outbound;
  const ret = booking.returnDriver ? personName(booking.returnDriver) : null;
  if (!outbound && !ret) return null;
  if (booking.driverPersonID === booking.returnDriverPersonID) return outbound;
  return `Outbound: ${outbound ?? "–"} · Return: ${ret ?? "–"}`;
}

export function formatBookingDates(booking: {
  startDate: number;
  endDate: number;
}) {
  const start = formatDate(new Date(booking.startDate));
  return booking.startDate === booking.endDate
    ? start
    : `${start} – ${formatDate(new Date(booking.endDate))}`;
}

/** Create or edit a vehicle booking or appointment. */
export function VehicleBookingDialog({
  open,
  onOpenChange,
  booking,
  defaults,
  fixedProjectId,
}: VehicleBookingDialogProps) {
  const { data: vehicles } = useVehicles();
  const { data: projects = [] } = useProjects(false);
  const { data: people = [] } = usePeople(["employee", "freelancer"]);
  const save = useSaveVehicleBooking();
  const remove = useDeleteVehicleBooking();

  const [vehicleID, setVehicleID] = useState("");
  const [category, setCategory] = useState<VehicleBookingCategory>("project");
  const [projectID, setProjectID] = useState("");
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [driverID, setDriverID] = useState("");
  // Project bookings: separate driver for the return trip; follows the
  // outbound driver until chosen separately
  const [returnDriverID, setReturnDriverID] = useState("");
  const [returnDriverEdited, setReturnDriverEdited] = useState(false);
  const [notes, setNotes] = useState("");
  // Outbound/return dates follow the project's event dates until edited
  const [outboundDate, setOutboundDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [travelDaysEdited, setTravelDaysEdited] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVehicleID(booking?.vehicleID ?? defaults?.vehicleID ?? "");
    setCategory(
      fixedProjectId
        ? "project"
        : (booking?.category ?? defaults?.category ?? "project"),
    );
    setProjectID(
      fixedProjectId ?? booking?.projectID ?? defaults?.projectID ?? "",
    );
    setTitle(booking?.title ?? "");
    setStartDate(isoDate(booking?.startDate) ?? defaults?.startDate ?? "");
    setEndDate(
      isoDate(booking?.endDate) ??
        defaults?.endDate ??
        defaults?.startDate ??
        "",
    );
    setDriverID(booking?.driverPersonID ?? "");
    setReturnDriverID(booking?.returnDriverPersonID ?? "");
    setReturnDriverEdited(
      !!booking &&
        (booking.returnDriverPersonID ?? null) !==
          (booking.driverPersonID ?? null),
    );
    setNotes(booking?.notes ?? "");
    setOutboundDate(isoDate(booking?.outboundDate) ?? "");
    setReturnDate(isoDate(booking?.returnDate) ?? "");
    setTravelDaysEdited(!!booking?.outboundDate || !!booking?.returnDate);
  }, [open, booking, defaults, fixedProjectId]);

  // Keep the end date on or after the start date
  const handleStartChange = (value: string) => {
    setStartDate(value);
    if (value && (!endDate || endDate < value)) setEndDate(value);
  };

  const isProject = category === "project";
  const datesValid = !!startDate && !!endDate && endDate >= startDate;

  // Default outbound/return dates: event dates of the project, else the booking
  const selectedProject = projects.find((project) => project.id === projectID);
  const eventStart =
    selectedProject?.event_start_date ?? selectedProject?.event_end_date;
  const eventEnd =
    selectedProject?.event_end_date ?? selectedProject?.event_start_date;
  // Stored travel days always win; this effect runs in the same commit as
  // the one loading the booking, so it must not rely on travelDaysEdited
  const hasStoredTravelDays = !!(booking?.outboundDate || booking?.returnDate);
  useEffect(() => {
    if (!open || !isProject || travelDaysEdited || hasStoredTravelDays) return;
    setOutboundDate(eventStart ?? startDate);
    setReturnDate(eventEnd ?? endDate);
  }, [
    open,
    isProject,
    travelDaysEdited,
    hasStoredTravelDays,
    eventStart,
    eventEnd,
    startDate,
    endDate,
  ]);
  const travelDaysValid =
    !isProject ||
    (!!outboundDate && !!returnDate && returnDate >= outboundDate);
  const vehicleName = vehicles.find(
    (vehicle) => vehicle.id === vehicleID,
  )?.name;

  // Other bookings of this vehicle in the chosen period
  const from = isoToZeroDate(startDate) ?? 0;
  const to = isoToZeroDate(endDate) ?? 0;
  const { data: rangeBookings } = useVehicleBookingsInRange(
    from,
    to,
    open && datesValid && !!vehicleID,
  );
  const conflicts = useMemo(
    () =>
      datesValid && vehicleID
        ? rangeBookings.filter(
            (other) =>
              other.id !== booking?.id &&
              other.vehicleID === vehicleID &&
              bookingsOverlap(other, {
                id: "",
                vehicleID,
                startDate: from,
                endDate: to,
              }),
          )
        : [],
    [rangeBookings, booking?.id, vehicleID, datesValid, from, to],
  );

  const vehicleOptions = vehicles.map((vehicle) => ({
    value: vehicle.id,
    label: vehicle.name,
    subtitle: [vehicle.licensePlate, vehicle.brand, vehicle.model]
      .filter(Boolean)
      .join(" · "),
  }));
  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: formatProjectName(project.name, project.event_start_date),
    subtitle: project.number ?? undefined,
  }));
  // Project bookings: only people booked on the project can drive
  const { data: availability = [], isLoading: availabilityLoading } =
    useProjectAvailability(isProject && projectID ? projectID : undefined);
  const bookedPeople = availability
    .filter((row) => row.status === "booked" && row.person)
    .map((row) => row.person!);
  const driverOptions = (
    isProject
      ? bookedPeople.map((person) => ({
          value: person.id,
          label: `${person.first_name} ${person.last_name}`,
          subtitle:
            person.person_type === "freelancer" ? "Freelancer" : undefined,
        }))
      : people.map((person) => ({
          value: person.id,
          label: `${person.first_name} ${person.last_name}`,
          subtitle:
            person.person_type === "freelancer" ? "Freelancer" : undefined,
        }))
  ).sort((a, b) => a.label.localeCompare(b.label));
  // Keep showing drivers who were set before but are no longer booked
  for (const [id, person] of [
    [booking?.driverPersonID, booking?.driver],
    [booking?.returnDriverPersonID, booking?.returnDriver],
  ] as const) {
    if (id && person && !driverOptions.some((option) => option.value === id)) {
      driverOptions.push({
        value: id,
        label: `${person.firstName} ${person.lastName}`,
        subtitle: "Not booked on this project",
      });
    }
  }
  const isNotBooked = (personId: string) =>
    isProject &&
    !!personId &&
    !availabilityLoading &&
    !bookedPeople.some((person) => person.id === personId);
  // A new or changed project driver must be booked on the project
  const driverNotBooked =
    isNotBooked(driverID) && driverID !== (booking?.driverPersonID ?? "");
  const returnDriverNotBooked =
    isNotBooked(returnDriverID) &&
    returnDriverID !== (booking?.returnDriverPersonID ?? "");

  const handleDriverChange = (value: string) => {
    setDriverID(value);
    if (!returnDriverEdited) setReturnDriverID(value);
  };

  const canSave =
    !!vehicleID &&
    datesValid &&
    travelDaysValid &&
    (isProject ? !!projectID : !!title.trim()) &&
    !driverNotBooked &&
    !returnDriverNotBooked &&
    !save.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    save.mutate(
      {
        id: booking?.id,
        vehicleID,
        category,
        projectID: isProject ? projectID : null,
        title: isProject ? null : title.trim(),
        startDate,
        endDate,
        outboundDate: isProject ? outboundDate : null,
        returnDate: isProject ? returnDate : null,
        driverPersonID: driverID || null,
        returnDriverPersonID: isProject ? returnDriverID || null : null,
        notes: notes.trim() || null,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {booking
                ? "Edit booking"
                : fixedProjectId
                  ? "Book vehicle"
                  : "New booking"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Vehicle</Label>
              <EntityCombobox
                options={vehicleOptions}
                value={vehicleID}
                onChange={(value) => setVehicleID(value ?? "")}
                placeholder="Select vehicle"
                searchPlaceholder="Search vehicles..."
                testId="booking-vehicle-picker"
              />
            </div>

            {!fixedProjectId && (
              <div className="space-y-2 sm:col-span-2">
                <Label>Type</Label>
                <Select
                  value={category}
                  onValueChange={(value) =>
                    setCategory(value as VehicleBookingCategory)
                  }
                >
                  <SelectTrigger data-testid="booking-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project">Project booking</SelectItem>
                    {(
                      Object.keys(
                        bookingCategoryLabels,
                      ) as VehicleBookingCategory[]
                    )
                      .filter((value) => value !== "project")
                      .map((value) => (
                        <SelectItem key={value} value={value}>
                          {bookingCategoryLabels[value]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isProject && !fixedProjectId && (
              <div className="space-y-2 sm:col-span-2">
                <Label>Project</Label>
                <EntityCombobox
                  options={projectOptions}
                  value={projectID}
                  onChange={(value) => setProjectID(value ?? "")}
                  placeholder="Select project"
                  searchPlaceholder="Search projects..."
                  testId="booking-project-picker"
                />
              </div>
            )}

            {!isProject && (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="booking-title">Title</Label>
                <Input
                  id="booking-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="e.g. Service at Garage Muster"
                  data-testid="booking-title-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="booking-start">From</Label>
              <DatePicker
                id="booking-start"
                value={startDate}
                onChange={handleStartChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-end">To</Label>
              <DatePicker
                id="booking-end"
                value={endDate}
                onChange={setEndDate}
              />
            </div>

            {isProject ? (
              <>
                {/* Outbound and return trip: date and driver each */}
                <div className="space-y-2">
                  <Label htmlFor="booking-outbound-date">Outbound date</Label>
                  <DatePicker
                    id="booking-outbound-date"
                    value={outboundDate}
                    onChange={(value) => {
                      setTravelDaysEdited(true);
                      setOutboundDate(value);
                      if (value && (!returnDate || returnDate < value)) {
                        setReturnDate(value);
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Outbound driver</Label>
                  <EntityCombobox
                    options={driverOptions}
                    value={driverID}
                    onChange={(value) => handleDriverChange(value ?? "")}
                    placeholder="No driver yet"
                    searchPlaceholder="Search people..."
                    allowClear
                    emptyMessage="Nobody booked on this project matches"
                    testId="booking-driver-picker"
                  />
                  {driverNotBooked && (
                    <p className="text-xs text-destructive">
                      This person is not booked on the project.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking-return-date">Return date</Label>
                  <DatePicker
                    id="booking-return-date"
                    value={returnDate}
                    onChange={(value) => {
                      setTravelDaysEdited(true);
                      setReturnDate(value);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Return driver</Label>
                  <EntityCombobox
                    options={driverOptions}
                    value={returnDriverID}
                    onChange={(value) => {
                      setReturnDriverEdited(true);
                      setReturnDriverID(value ?? "");
                    }}
                    placeholder="No driver yet"
                    searchPlaceholder="Search people..."
                    allowClear
                    emptyMessage="Nobody booked on this project matches"
                    testId="booking-return-driver-picker"
                  />
                  {returnDriverNotBooked && (
                    <p className="text-xs text-destructive">
                      This person is not booked on the project.
                    </p>
                  )}
                </div>
                <p
                  className="-mt-2 text-xs text-muted-foreground sm:col-span-2"
                  data-testid="booking-driver-hint"
                >
                  {!projectID
                    ? "Select a project to choose drivers."
                    : bookedPeople.length === 0 && !availabilityLoading
                      ? "Nobody is booked on this project yet. Book people in the project's People tab first."
                      : `Only people booked on this project. The drivers appear in the People tab under “Driver” on the outbound and return date${
                          vehicleName ? ` (with ${vehicleName})` : ""
                        }. Dates default to the event dates.`}
                </p>
              </>
            ) : (
              <div className="space-y-2 sm:col-span-2">
                <Label>Driver</Label>
                <EntityCombobox
                  options={driverOptions}
                  value={driverID}
                  onChange={(value) => handleDriverChange(value ?? "")}
                  placeholder="No driver yet"
                  searchPlaceholder="Search people..."
                  allowClear
                  testId="booking-driver-picker"
                />
              </div>
            )}

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="booking-notes">Notes</Label>
              <Textarea
                id="booking-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>

            {startDate && endDate && endDate < startDate && (
              <p className="text-sm text-destructive sm:col-span-2">
                The end date must not be before the start date.
              </p>
            )}

            {conflicts.length > 0 && (
              <div
                className="rounded-md border border-amber-500 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100 sm:col-span-2"
                data-testid="booking-conflict-warning"
              >
                <p className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  This vehicle is already booked in this period:
                </p>
                <ul className="mt-1 list-disc pl-6">
                  {conflicts.map((other) => (
                    <li key={other.id}>
                      {bookingLabel(other)} ({formatBookingDates(other)})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {booking && (
              <Button
                type="button"
                variant="destructive"
                className="mr-auto"
                onClick={() => {
                  if (confirm("Delete this booking?")) {
                    remove.mutate(booking.id, {
                      onSuccess: () => onOpenChange(false),
                    });
                  }
                }}
                data-testid="delete-booking"
              >
                Delete
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSave}
              data-testid="save-booking"
            >
              {conflicts.length > 0 ? "Save anyway" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
